import { useState, useEffect, type FormEvent } from "react"
import { Building2, Loader2, ShieldCheck } from "lucide-react"
import { cn } from "@lib/utils/helpers"
import { COMM_FIELD_BASE, COMM_LABEL } from "@/lib/styles/companyEditTabShell"
import { MultiDropdown } from "@/components/ui/MultiDropdown"
import { useUserRole } from "@/hooks/useUserRole"
import { showSuccess, showError, formatApiError } from "@/lib/utils/notifications"
import { isCompanyDuplicateAddressCancelledError } from "@/features/company/utils/companyDuplicateAddress"
import {
    applyPreferencesOtherClearAngularStyle,
    buildCompanyEditSaveParityInput,
    companyEditSaveParitySchema,
    getFilterValueAngularStyle,
    mergeAngularFullFormParityFields,
} from "../../utils/companyValidators"
import { getCompanyConfig } from "../../services/companyService"
import { SectionCard } from "@/components/ui/SectionCard"
import tabStyles from "../../styles/companyInfoTab.module.css"

import type { PreferencesTabProps } from "../../types"

/**
 * Mirrors Angular `company-edit.component.ts` / `company-add.component.ts` `getConfig()`:
 * `GET /api/company_config` → `response.property_mgmt_system` / `response.resident_screening` arrays,
 * then append `{ label: 'Other', value: 'Other' }` (here: push `"Other"`).
 */
function buildDropdownOptionsFromCompanyConfig(response: {
    property_mgmt_system?: unknown
    resident_screening?: unknown
}): { propertyMgmtOptions: string[]; residentScreeningOptions: string[] } {
    const propertyMgmtOptions: string[] = []
    if (Array.isArray(response?.property_mgmt_system)) {
        response.property_mgmt_system.forEach((type: unknown) => {
            if (type != null && String(type).trim() !== "") {
                propertyMgmtOptions.push(String(type).trim())
            }
        })
    }
    propertyMgmtOptions.push("Other")

    const residentScreeningOptions: string[] = []
    if (Array.isArray(response?.resident_screening)) {
        response.resident_screening.forEach((type: unknown) => {
            if (type != null && String(type).trim() !== "") {
                residentScreeningOptions.push(String(type).trim())
            }
        })
    }
    residentScreeningOptions.push("Other")

    return { propertyMgmtOptions, residentScreeningOptions }
}

/** Map API / saved values to canonical option labels so chips and list stay in sync. */
function normalizeToOptionLabels(raw: string[], optionList: string[]): string[] {
    const byLower = new Map(optionList.map((o) => [o.toLowerCase(), o]))
    const out: string[] = []
    const seen = new Set<string>()
    for (const v of raw) {
        const t = typeof v === "string" ? v.trim() : String(v)
        if (!t) continue
        const canonical = byLower.get(t.toLowerCase()) ?? t
        if (!seen.has(canonical)) {
            seen.add(canonical)
            out.push(canonical)
        }
    }
    return out
}

export function PreferencesTab({ company, onUpdate }: PreferencesTabProps) {
    const { hasPolicy } = useUserRole()
    const canEdit = hasPolicy(["company_preference_edit"])
    const canEditLeadDays = hasPolicy(["company_lead_days_edit"])

    const [propertyMgmtOptions, setPropertyMgmtOptions] = useState<string[]>([])
    const [residentScreeningOptions, setResidentScreeningOptions] = useState<string[]>([])
    const [configLoading, setConfigLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: loading state for the company-config fetch (data-load effect)
        setConfigLoading(true)
        getCompanyConfig()
            .then((response) => {
                if (cancelled || !response) return
                const { propertyMgmtOptions: pms, residentScreeningOptions: rs } =
                    buildDropdownOptionsFromCompanyConfig(response)
                setPropertyMgmtOptions(pms)
                setResidentScreeningOptions(rs)
            })
            .catch((err: unknown) => {
                showError(formatApiError(err))
                setPropertyMgmtOptions(["Other"])
                setResidentScreeningOptions(["Other"])
            })
            .finally(() => {
                if (!cancelled) setConfigLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [])

    const parseCommaSeparated = (val: unknown): string[] => {
        if (Array.isArray(val)) return val.map((v) => String(v).trim()).filter(Boolean)
        if (typeof val === "string" && val) return val.split(",").map((s) => s.trim()).filter(Boolean)
        return []
    }

    const buildFormFromCompany = (c: typeof company) => ({
        property_mgmt_system: normalizeToOptionLabels(
            parseCommaSeparated(c?.property_mgmt_system),
            propertyMgmtOptions
        ),
        property_mgmt_system_other: c?.property_mgmt_system_other || "",
        resident_screening: normalizeToOptionLabels(
            parseCommaSeparated(c?.resident_screening),
            residentScreeningOptions
        ),
        resident_screening_other: c?.resident_screening_other || "",
        lease_start_lead_days: c?.lease_start_lead_days != null ? String(c.lease_start_lead_days) : "",
    })

    /** Only re-sync when server-backed preference fields actually change — not on every new `company` object reference. */
    const preferencesServerSyncKey = [
        company?.company_id ?? company?.id ?? "",
        typeof company?.property_mgmt_system === "string"
            ? company.property_mgmt_system
            : JSON.stringify(company?.property_mgmt_system ?? null),
        company?.property_mgmt_system_other ?? "",
        typeof company?.resident_screening === "string"
            ? company.resident_screening
            : JSON.stringify(company?.resident_screening ?? null),
        company?.resident_screening_other ?? "",
        company?.lease_start_lead_days != null ? String(company.lease_start_lead_days) : "",
    ].join("\u001f")

    const [formData, setFormData] = useState(() => buildFormFromCompany(company))
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: re-sync the form to the company prop when server data/options change
        setFormData(buildFormFromCompany(company))
        setFieldErrors({})
    }, [preferencesServerSyncKey, propertyMgmtOptions, residentScreeningOptions])


    /**
     * Angular `submitForm` / `editCompany()`: validate full `companyEditForm` (not Preferences-only) →
     * `getFilterValue(form)` → force `*_other = ''` when multiselect non-empty without Other →
     * `multiSelect('property_mgmt_system' / 'resident_screening')`.
     * Do not spread GET `company` into POST; parity keys via `mergeAngularFullFormParityFields`.
     */
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        const validated = companyEditSaveParitySchema.safeParse(
            buildCompanyEditSaveParityInput(company as Record<string, unknown> | undefined, formData),
        )
        if (!validated.success) {
            const next: Record<string, string> = {}
            for (const issue of validated.error.issues) {
                const key = issue.path[0]
                if (typeof key === "string" && !next[key]) next[key] = issue.message
            }
            setFieldErrors(next)
            showError("Please complete required fields.")
            return
        }
        setFieldErrors({})
        try {
            const pms = validated.data.property_mgmt_system
            const rs = validated.data.resident_screening
            const tabFields: Record<string, unknown> = {
                property_mgmt_system: pms,
                resident_screening: rs,
                // Only keep Other text when Other is selected; empty multiselect must not POST leftover *_other.
                property_mgmt_system_other: pms.includes("Other")
                    ? (validated.data.property_mgmt_system_other ?? "")
                    : "",
                resident_screening_other: rs.includes("Other")
                    ? (validated.data.resident_screening_other ?? "")
                    : "",
                lease_start_lead_days: validated.data.lease_start_lead_days,
            }
            const filtered = getFilterValueAngularStyle(tabFields) as Record<string, unknown>
            // Angular sets `*_other = ''` after getFilterValue when options remain without Other.
            applyPreferencesOtherClearAngularStyle(filtered)
            const payload = mergeAngularFullFormParityFields(company as Record<string, unknown> | undefined, filtered)
            await onUpdate(payload)
            showSuccess("Company Updated  Successfully!")
        } catch (err: unknown) {
            if (isCompanyDuplicateAddressCancelledError(err)) return
            showError(formatApiError(err))
        }
    }

    return (
        <form
            data-testid="company-preferences-tab-form"
            onSubmit={handleSubmit}
            data-tab-id="preferences"
            className={tabStyles.tabContent}
        >
            <SectionCard
                icon={Building2}
                title="Property Mgmt System"
                testId="company-preferences-property-mgmt-section"
            >
                <div className="w-full min-w-0 max-w-xl">
                    {configLoading ? (
                        <p className="mb-3 flex items-center gap-1.5 text-xs text-[rgb(21_151_255)]">
                            <Loader2 size={12} className="animate-spin" aria-hidden />
                            Loading options…
                        </p>
                    ) : null}
                    <label className={COMM_LABEL}>
                        Property Mgmt System
                    </label>
                    <div className="w-full min-w-0">
                        <MultiDropdown
                            chipRemoveTestIdPrefix="company-preferences-property-mgmt-chip-remove"
                            triggerDataTestId="company-preferences-property-mgmt-trigger"
                            options={propertyMgmtOptions}
                            selected={formData.property_mgmt_system}
                            onChange={(val) => {
                                setFieldErrors((prev) => {
                                    const { property_mgmt_system_other: _, ...rest } = prev
                                    return rest
                                })
                                // Angular `configChanges`: re-selecting Other adds `new FormControl('')`.
                                setFormData((prev) => {
                                    const newlySelectedOther =
                                        val.includes("Other") && !prev.property_mgmt_system.includes("Other")
                                    return {
                                        ...prev,
                                        property_mgmt_system: val,
                                        property_mgmt_system_other: newlySelectedOther
                                            ? ""
                                            : prev.property_mgmt_system_other,
                                    }
                                })
                            }}
                            placeholder={configLoading ? "Loading…" : "Select property management system(s)"}
                            disabled={!canEdit || configLoading}
                        />
                    </div>
                    {formData.property_mgmt_system.includes("Other") ? (
                        <div className="mt-3 w-full min-w-0">
                            <label htmlFor="company-preferences-property-mgmt-other" className={COMM_LABEL}>
                                Other <span className="font-semibold text-[#dc2626]">*</span>
                            </label>
                            <input
                                id="company-preferences-property-mgmt-other"
                                data-testid="company-preferences-property-mgmt-other-input"
                                type="text"
                                value={formData.property_mgmt_system_other}
                                onChange={(e) => {
                                    setFieldErrors((prev) => {
                                        const { property_mgmt_system_other: _, ...rest } = prev
                                        return rest
                                    })
                                    setFormData((prev) => ({
                                        ...prev,
                                        property_mgmt_system_other: e.target.value,
                                    }))
                                }}
                                disabled={!canEdit}
                                placeholder="Specify other property management system"
                                aria-invalid={!!fieldErrors.property_mgmt_system_other}
                                className={cn(
                                    COMM_FIELD_BASE,
                                    "h-10 w-full",
                                    fieldErrors.property_mgmt_system_other
                                        ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500/20"
                                        : ""
                                )}
                            />
                            {fieldErrors.property_mgmt_system_other ? (
                                <p className="mt-1 text-xs text-rose-600">
                                    {fieldErrors.property_mgmt_system_other}
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    <div className="mt-4 w-full min-w-0">
                        <label htmlFor="company-preferences-lease-start-lead-days" className={COMM_LABEL}>
                            Lease Start Lead Time (days)
                        </label>
                        <input
                            id="company-preferences-lease-start-lead-days"
                            data-testid="company-preferences-lease-start-lead-days-input"
                            type="number"
                            min={0}
                            onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E" || e.key === ".") {
                                    e.preventDefault();
                                }
                            }}
                            value={formData.lease_start_lead_days}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "") {
                                    setFormData((prev) => ({
                                        ...prev,
                                        lease_start_lead_days: "",
                                    }))
                                    return;
                                }
                                const num = parseInt(val, 10);
                                if (!isNaN(num) && num >= 0) {
                                    setFormData((prev) => ({
                                        ...prev,
                                        lease_start_lead_days: String(num),
                                    }))
                                }
                            }}
                            disabled={!canEdit || !canEditLeadDays}
                            placeholder="Lease start lead time in days"
                            className={cn(
                                COMM_FIELD_BASE,
                                "h-10 w-full max-w-[150px] tabular-nums no-spinners",
                                fieldErrors.lease_start_lead_days
                                    ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500/20"
                                    : ""
                            )}
                        />
                        {fieldErrors.lease_start_lead_days ? (
                            <p className="mt-1 text-xs text-rose-600">
                                {fieldErrors.lease_start_lead_days}
                            </p>
                        ) : null}
                    </div>
                </div>
            </SectionCard>

            <SectionCard
                icon={ShieldCheck}
                title="Resident Screening"
                testId="company-preferences-resident-screening-section"
            >
                <div className="w-full min-w-0 max-w-xl">
                    {configLoading ? (
                        <p className="mb-3 flex items-center gap-1.5 text-xs text-[rgb(21_151_255)]">
                            <Loader2 size={12} className="animate-spin" aria-hidden />
                            Loading options…
                        </p>
                    ) : null}
                    <label className={COMM_LABEL}>
                        Resident Screening
                    </label>
                    <div className="w-full min-w-0">
                        <MultiDropdown
                            chipRemoveTestIdPrefix="company-preferences-resident-screening-chip-remove"
                            triggerDataTestId="company-preferences-resident-screening-trigger"
                            options={residentScreeningOptions}
                            selected={formData.resident_screening}
                            onChange={(val) => {
                                setFieldErrors((prev) => {
                                    const { resident_screening_other: _, ...rest } = prev
                                    return rest
                                })
                                setFormData((prev) => {
                                    const newlySelectedOther =
                                        val.includes("Other") && !prev.resident_screening.includes("Other")
                                    return {
                                        ...prev,
                                        resident_screening: val,
                                        resident_screening_other: newlySelectedOther
                                            ? ""
                                            : prev.resident_screening_other,
                                    }
                                })
                            }}
                            placeholder={configLoading ? "Loading…" : "Select resident screening provider(s)"}
                            disabled={!canEdit || configLoading}
                        />
                    </div>
                    {formData.resident_screening.includes("Other") ? (
                        <div className="mt-3 w-full min-w-0">
                            <label htmlFor="company-preferences-resident-screening-other" className={COMM_LABEL}>
                                Other <span className="font-semibold text-[#dc2626]">*</span>
                            </label>
                            <input
                                id="company-preferences-resident-screening-other"
                                data-testid="company-preferences-resident-screening-other-input"
                                type="text"
                                value={formData.resident_screening_other}
                                onChange={(e) => {
                                    setFieldErrors((prev) => {
                                        const { resident_screening_other: _, ...rest } = prev
                                        return rest
                                    })
                                    setFormData((prev) => ({
                                        ...prev,
                                        resident_screening_other: e.target.value,
                                    }))
                                }}
                                disabled={!canEdit}
                                placeholder="Specify other resident screening provider"
                                aria-invalid={!!fieldErrors.resident_screening_other}
                                className={cn(
                                    COMM_FIELD_BASE,
                                    "h-10 w-full",
                                    fieldErrors.resident_screening_other
                                        ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500/20"
                                        : ""
                                )}
                            />
                            {fieldErrors.resident_screening_other ? (
                                <p className="mt-1 text-xs text-rose-600">
                                    {fieldErrors.resident_screening_other}
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </SectionCard>

            {/* Header Submit → Confirm → requestSubmit(); target must be a submit control (matches Contacts tab). */}
            <button
                type="submit"
                id="preferences-hidden-submit"
                data-testid="company-preferences-hidden-submit"
                tabIndex={-1}
                aria-hidden
                style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
            />
        </form>
    )
}
